import React, { RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import Script from 'next/script';
import { ThemeProvider } from 'styled-components';

import { CDN_API_ENDPOINT, CHAT_FRAME_CDN, IS_GTM_DISABLED } from '@r1-frontend/entities/Layout/_deps/constants';
import isSso from '@r1-frontend/entities/Layout/_deps/helpers/isSso';
import App from '@r1-frontend/entities/Layout/App';
import Head, { IHead } from '@r1-frontend/entities/Layout/Head';
import { useHeaderScroll } from '@r1-frontend/entities/Layout/Header/useHeaderScroll';
import SeoOrganizationScript from '@r1-frontend/entities/Layout/SeoScripts/SeoOrganizationScript';
import { verifyAuthState } from '@r1-frontend/entities/stores/auth/actions/verifyAuthState';
import { TState } from '@r1-frontend/entities/stores/auth/reducer';

import { defaultErrorHandler, init } from '@r1-frontend/analytics/campaignLayer';

import { lightTheme } from '@r1-frontend/ui-kit/themes';
import { isClientSide } from '@r1-frontend/shared/helpers/ssr';

import { AppContext } from './App/AppContext';

const ymId = 30080914;

const NO_SCROLL_PATH = [
    '/internet',
    '/internet/mono-village',
    '/full-buy',
];

const CookieAlert = dynamic(() => import('@r1-frontend/ui-react/organisms/CookieAlert'), { ssr: false });

interface ILayout extends IHead {
    withMenu?: boolean,
    withFooter?: boolean,
    withChat?: boolean,
    withBottomSticky?: RefObject<HTMLDivElement>,
    bgColor?: string,
    withEmailWidget?: boolean,
    emailWidgetText?: string,
    withShadowInMobileHeader?: boolean,
    withHeaderOffset?: boolean,
    children: JSX.Element | JSX.Element[] | null,
}

const Layout = ({
    children,
    withMenu = true,
    withFooter = true,
    withChat = true,
    bgColor,
    withShadowInMobileHeader = true,
     ...props
}: ILayout) => {
    const isClient = isClientSide();

    const dispatch = useDispatch();

    const { isAuth, clientPersonal } = useSelector((state: TState) => state.auth);
    const { agreement } = clientPersonal || {};

    useEffect(() => {
        const checkCookieInterval = setInterval(() => {
            dispatch(verifyAuthState(isSso));
        }, 1000);

        return () => {
            clearInterval(checkCookieInterval);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!agreement) {
            return;
        }

        if (isAuth && agreement) {
            const intervalId = setInterval(() => {
                window.ym && window.ym(
                    ymId,
                    'userParams',
                    { UserID: agreement },
                );
                clearInterval(intervalId);
            }, 1000);

            return () => {
                clearInterval(intervalId);
            };
        }
    }, [isAuth, agreement]);

    useEffect(() => {
        if (agreement) {
            init({
                agreementNumber: agreement,
                onError: defaultErrorHandler,
                debug: process.env.NEXT_PUBLIC_ENVIRONMENT === 'localhost',
            });
        }
    }, [agreement]);

    const { isScrollingUp } = useHeaderScroll();

    return (
        <ThemeProvider theme={lightTheme}>
            <Head {...props} />
            <AppContext.Provider value={{ withShadowInMobileHeader, isScrollingUp }}>
                <App
                    bgColor={bgColor}
                    withMenu={withMenu}
                    withFooter={withFooter}
                    withChat={withChat}
                    {...props}
                >
                    <CookieAlert/>

                    {isClient && (
                        IS_GTM_DISABLED
                            ? <Script async src={`${CDN_API_ENDPOINT}/static/js/gtmDevStub.js`}/>
                            : <Script async src={`${CDN_API_ENDPOINT}/static/js/googleTagManager.js`}/>
                    )}
                    <Script id='disable-speedy'>window.SC_DISABLE_SPEEDY = true</Script>
                    <Script async src={`${CHAT_FRAME_CDN}/chat-frame.min.js`}/>
                    {children}
                </App>
                <SeoOrganizationScript/>
            </AppContext.Provider>
        </ThemeProvider>
    );
};

// TODO Проверить актуальность этого решения. Меняет поведение роутинга по дефолту, сложно найти
Router.events.on('routeChangeComplete', (pathname) => {
    typeof window.flocktory?.reInit === 'function' && window.flocktory.reInit();

    const [path] = pathname.split('?');
    if (NO_SCROLL_PATH.includes(path)) {
        return;
    }
    window.scrollTo(0, 0);
});

export default Layout;
